/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { Clock, Calendar, CheckCircle2, AlertTriangle } from 'lucide-react';

interface CountdownTimerProps {
  startDate?: string;
  endDate?: string;
  cycleId?: string;
  status?: 'active' | 'paused' | 'completed' | 'inactive' | string;
  isActive?: boolean;
  lang?: 'en' | 'ha';
  onPauseToggle?: () => void;
  // Canonical data from server
  daysRemaining?: number;
  hoursRemaining?: number;
  minutesRemaining?: number;
  secondsRemaining?: number;
  progressPercent?: number;
  currentDay?: number;
  totalCycleDays?: number;
  // Additional stats
  drivers?: number;
  fleet?: number;
  remit?: number;
  health?: string;
  cycleDay?: string;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  startDate,
  endDate,
  cycleId = 'No Active Cycle',
  status = 'inactive',
  isActive = false,
  lang = 'en',
  onPauseToggle,
  daysRemaining = 0,
  hoursRemaining = 0,
  minutesRemaining = 0,
  secondsRemaining = 0,
  progressPercent = 0,
  currentDay = 0,
  totalCycleDays = 30,
  drivers = 0,
  fleet = 0,
  remit = 0,
  health = 'N/A',
  cycleDay = '0'
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(lang === 'ha' ? 'ha-NG' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  };

  // Determine if cycle is truly active based on props
  const cycleIsActive = isActive && status !== 'inactive' && status !== 'completed';

  const [timeLeft, setTimeLeft] = useState({
    days: String(daysRemaining).padStart(2, '0'),
    hours: String(hoursRemaining).padStart(2, '0'),
    minutes: String(minutesRemaining).padStart(2, '0'),
    seconds: String(secondsRemaining).padStart(2, '0'),
    progress: progressPercent
  });

  // Local tick to keep UI smooth between server updates
  useEffect(() => {
    if (!cycleIsActive || status === 'paused') {
      setTimeLeft({
        days: String(daysRemaining).padStart(2, '0'),
        hours: String(hoursRemaining).padStart(2, '0'),
        minutes: String(minutesRemaining).padStart(2, '0'),
        seconds: String(secondsRemaining).padStart(2, '0'),
        progress: progressPercent
      });
      return;
    }

    let totalSecs = (daysRemaining * 24 * 3600) + (hoursRemaining * 3600) + (minutesRemaining * 60) + secondsRemaining;
    
    const tick = () => {
      if (totalSecs <= 0) return;
      totalSecs--;
      
      const d = Math.floor(totalSecs / (24 * 3600));
      const h = Math.floor((totalSecs % (24 * 3600)) / 3600);
      const m = Math.floor((totalSecs % 3600) / 60);
      const s = totalSecs % 60;
      
      const pad = (num: number) => String(num).padStart(2, '0');
      
      setTimeLeft(prev => ({
        days: pad(d),
        hours: pad(h),
        minutes: pad(m),
        seconds: pad(s),
        progress: prev.progress // Progress updated by server
      }));
    };

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [daysRemaining, hoursRemaining, minutesRemaining, secondsRemaining, cycleIsActive, status, progressPercent]);

  const units = [
    { label: lang === 'ha' ? 'KWANAKI' : 'DAYS', value: timeLeft.days },
    { label: lang === 'ha' ? 'AWARI' : 'HRS', value: timeLeft.hours },
    { label: lang === 'ha' ? 'MINTOCI' : 'MINS', value: timeLeft.minutes },
    { label: lang === 'ha' ? 'DAKIKU' : 'SECS', value: timeLeft.seconds }
  ];

  // Render Inactive Banner State when cycle is not running
  if (!cycleIsActive) {
    return (
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs p-4 flex flex-col gap-3 w-full">
        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-slate-50 border border-slate-200/80 rounded-lg shadow-2xs">
              <Clock className="h-4 w-4 text-slate-400" />
            </div>
            <div>
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider font-mono">
                {lang === 'ha' ? 'KIDAYAR ZAGAYEN AIKI' : 'ACTIVE CYCLE TIMER'}
              </h3>
              <p className="text-[10px] text-slate-400 font-semibold leading-none mt-0.5">
                {lang === 'ha' ? `Kidayar kwanaki ${totalCycleDays}` : `${totalCycleDays}-Day countdown status`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700 text-[9px] font-black font-mono">
              {cycleId}
            </span>
            <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border bg-amber-50 border-amber-200 text-amber-600">
              {lang === 'ha' ? 'BA A FARA BA' : 'INACTIVE'}
            </span>
          </div>
        </div>

        <div className="p-3 bg-amber-50/80 border border-amber-200/70 rounded-xl flex items-center gap-2.5 text-xs text-amber-800 font-medium">
          <AlertTriangle className="h-4.5 w-4.5 text-amber-600 shrink-0" />
          <span>
            {lang === 'ha'
              ? 'Babu zagayen aiki mai gudana yanzu. Fara aiki daga bangaren gudanarwa don buɗe kidayar kwanaki 30.'
              : 'No active operating cycle. Start operations from the Enterprise System card to begin the 30-day countdown.'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2 bg-slate-50/80 border border-slate-100 rounded-xl p-2.5 text-xs">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-slate-100 text-slate-500 rounded-lg border border-slate-200/60 shrink-0">
              <Calendar className="h-3.5 w-3.5" />
            </div>
            <div>
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider block leading-none">
                {lang === 'ha' ? 'LOKACIN FARA' : 'START TIME'}
              </span>
              <span className="font-extrabold text-slate-800 font-mono text-[11px] leading-none mt-1 block">
                {formatDate(startDate) || 'N/A'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 pl-2 border-l border-slate-200/60">
            <div className="p-1.5 bg-slate-100 text-slate-500 rounded-lg border border-slate-200/60 shrink-0">
              <CheckCircle2 className="h-3.5 w-3.5" />
            </div>
            <div>
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider block leading-none">
                {lang === 'ha' ? 'RANAR KAMMALAWA' : 'SCHEDULED END'}
              </span>
              <span className="font-extrabold text-slate-800 font-mono text-[11px] leading-none mt-1 block">
                {formatDate(endDate) || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Debug / Full Info View for Inactive State */}
        {(drivers > 0 || fleet > 0 || remit > 0) && (
          <div className="grid grid-cols-3 gap-1.5 mt-1 border-t border-slate-100 pt-2.5">
            <div className="flex flex-col">
              <span className="text-[7px] font-bold text-slate-400 uppercase tracking-tight">Drivers</span>
              <span className="text-[10px] font-black text-slate-700">{drivers}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[7px] font-bold text-slate-400 uppercase tracking-tight">Fleet</span>
              <span className="text-[10px] font-black text-slate-700">{fleet}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[7px] font-bold text-slate-400 uppercase tracking-tight">Remit</span>
              <span className="text-[10px] font-black text-emerald-600">₦{remit.toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs p-4 flex flex-col gap-3 w-full">
      {/* Card Header */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-slate-50 border border-slate-200/80 rounded-lg shadow-2xs">
            <Clock className="h-4 w-4 text-brand-gold animate-pulse" />
          </div>
          <div>
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider font-mono">
              {lang === 'ha' ? 'KIDAYAR ZAGAYEN AIKI' : 'ACTIVE CYCLE TIMER'}
            </h3>
            <p className="text-[10px] text-slate-400 font-semibold leading-none mt-0.5">
              {lang === 'ha' ? `Kidayar kwanaki ${totalCycleDays} tare da sarrafa dakatarwa` : `${totalCycleDays}-Day countdown with freeze control`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-600 text-[9px] font-black font-mono">
            {cycleId}
          </span>
          <span
            className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border ${
              status === 'paused'
                ? 'bg-amber-50 border-amber-200 text-amber-600 animate-pulse'
                : 'bg-emerald-50 border-emerald-200 text-emerald-600 animate-pulse'
            }`}
          >
            {status === 'paused'
              ? lang === 'ha' ? 'AN DAKATAR' : 'PAUSED'
              : lang === 'ha' ? 'A-AIKI' : 'ACTIVE'}
          </span>
        </div>
      </div>

      {/* Timer LCD Display Grid */}
      <div className="grid grid-cols-4 gap-2 my-1">
        {units.map((unit, index) => (
          <div
            key={index}
            className="flex flex-col items-center justify-center p-2 sm:p-2.5 bg-slate-950 border border-slate-800 rounded-xl shadow-xs relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-radial-gradient from-slate-900 via-transparent to-transparent opacity-40 pointer-events-none" />
            <span className="text-xl sm:text-2xl font-black font-mono text-amber-400 tracking-tight leading-none">
              {unit.value}
            </span>
            <span className="text-[8px] sm:text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1 leading-none">
              {unit.label}
            </span>
          </div>
        ))}
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden border border-slate-200/60">
        <div
          className="bg-amber-400 h-full rounded-full transition-all duration-1000 ease-linear"
          style={{ width: `${timeLeft.progress}%` }}
        />
      </div>

      {/* Start Time and Scheduled End Footer */}
      <div className="grid grid-cols-2 gap-2 bg-slate-50/80 border border-slate-100 rounded-xl p-2.5 text-xs">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-200/60 shrink-0">
            <Calendar className="h-3.5 w-3.5" />
          </div>
          <div>
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider block leading-none">
              {lang === 'ha' ? 'LOKACIN FARA' : 'START TIME'}
            </span>
            <span className="font-extrabold text-slate-800 font-mono text-[11px] leading-none mt-1 block">
              {formatDate(startDate) || 'N/A'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 pl-2 border-l border-slate-200/60">
          <div className="p-1.5 bg-rose-50 text-rose-600 rounded-lg border border-rose-200/60 shrink-0">
            <CheckCircle2 className="h-3.5 w-3.5" />
          </div>
          <div>
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider block leading-none">
              {lang === 'ha' ? 'RANAR KAMMALAWA' : 'SCHEDULED END'}
            </span>
            <span className="font-extrabold text-slate-800 font-mono text-[11px] leading-none mt-1 block">
              {formatDate(endDate) || 'N/A'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
